import AssessmentJTFCharts from '@components/Charts/AssessmentJTFCharts'
import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useSlugLinks } from '@hooks/useSlugLinks'
import { useQuery } from '@tanstack/react-query'
import { GetAPIparams, useDropdownMenu } from '@utils/assessmentReportUtils'
import { Flex } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { useLocation } from '@suntex.core/nav'
import {
  Database_Procs_JTFAggregatedByLevel_Item,
  Database_Procs_TeamJTF_Item,
  TeamId
} from '@suntex.fim/api-client'
import {
  getAssessmentReportData,
  getValidAssessmentOption,
  schoolAssessmentInitialSorting,
  teamAssessmentInitialSorting
} from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import {
  $ArithmeticOperation,
  $EntityLevel,
  $JtfMode,
  EntityLevel
} from '@suntex.fim/enums'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import {
  ExportMenu,
  LevelSelect,
  LoaderOverlay,
  SearchInput
} from '@suntex.fim/ui'

import { appSessionStorage } from '../../utils/storageUtils'

import {
  columns,
  hidableSelectLabel,
  schoolColumns,
  schoolHidableSelectLabel
} from './AssessmentReport/AssessmentReportJTFTable'
import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

const jtfLevelOptions = $JtfMode.listOptions()
const operationOptions = $ArithmeticOperation.listOptions()

const activeColumns = [
  ['add_percent_pre', 'add_percent_post', 'add_percent_best'],
  ['sub_percent_pre', 'sub_percent_post', 'sub_percent_best'],
  ['mul_percent_pre', 'mul_percent_post', 'mul_percent_best'],
  ['div_percent_pre', 'div_percent_post', 'div_percent_best']
]

const newHidableSelectLabel: Record<string, string> = {
  ...hidableSelectLabel,
  add_percent_pre: 'Add Pre',
  add_percent_post: 'Add Post',
  add_percent_best: 'Add Best',
  sub_percent_pre: 'Sub Pre',
  sub_percent_post: 'Sub Post',
  sub_percent_best: 'Sub Best',
  mul_percent_pre: 'Mul Pre',
  mul_percent_post: 'Mul Post',
  mul_percent_best: 'Mul Best',
  div_percent_pre: 'Div Pre',
  div_percent_post: 'Div Post',
  div_percent_best: 'Div Best'
}
/**
 * returns JSX for just the fact report
 */
function AssessmentReportJTF() {
  const { api } = useFramework()
  useRotateDevice()
  const location = useLocation()
  const {
    jtfLevel: queryJtfLevel,
    jtfOperation: queryJtfOperation,
    grade: queryGrade,
    year: queryYear
  } = useAppQueryString()
  const { getGameUrl, isLoading: isSlugLinksLoading } = useSlugLinks()
  const team = useOptionalTeamInfo()
  const { login } = useAuth(isAuthenticated)
  const isSchool =
    login?.entityLevel === EntityLevel.School &&
    location.pathname.includes('school')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [dropdownFilters, setDropDownFilters] = useAssessmentDropdownFilters()
  const { id, year, grade } = GetAPIparams(isSchool, dropdownFilters)
  const [filters, setFilters] = useState(() => {
    return {
      level: getValidAssessmentOption(
        jtfLevelOptions,
        $JtfMode.from(queryJtfLevel ?? appSessionStorage.jtfLevel)
      ),
      operation: getValidAssessmentOption(
        operationOptions,
        $ArithmeticOperation.from(
          queryJtfOperation ?? appSessionStorage.jtfOperation
        )
      )
    }
  })
  const [sendMessageData, setSendMessageData] = useState<unknown[]>([])
  const linkOptions = operationOptions.map((operation) => {
    const url = getGameUrl([
      'Just the Facts',
      operation.label,
      jtfLevelOptions[filters.level]?.label ?? ''
    ])

    return {
      key: url,
      label: hidableSelectLabel[operation.label] ?? operation.label
    }
  })
  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: unknown[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }

  const memoColumns = useMemo<
    ColumnType<
      Database_Procs_TeamJTF_Item | Database_Procs_JTFAggregatedByLevel_Item
    >[]
  >(() => {
    if (isSchool) {
      // Ensure schoolColumns() returns the correct type
      return schoolColumns() as ColumnType<
        Database_Procs_TeamJTF_Item | Database_Procs_JTFAggregatedByLevel_Item
      >[]
    }
    // Ensure columns() returns the correct type
    return columns({ sendMessage }) as ColumnType<
      Database_Procs_TeamJTF_Item | Database_Procs_JTFAggregatedByLevel_Item
    >[]
  }, [filters.level])

  const { data: summaryRecordData = [] } = useQuery({
    queryKey: [
      'assessment',
      'jtfSummary',
      id,
      isSchool,
      filters.level,
      filters.operation,
      year,
      grade
    ],
    queryFn: async () => {
      const response = await api.get_Assessment_JustTheFactsSummary({
        id: id!,
        level: isSchool ? login.entityLevel : EntityLevel.Team,
        jtfMode: filters.level,
        operation: filters.operation,
        year: year!,
        grade
      })
      return response.jtfGraphs || []
    },
    enabled: id != null && year != null
  })

  const { data: reportData, isLoading } = useQuery({
    queryKey: [
      'assessment',
      'jtf',
      id,
      isSchool,
      filters.level,
      filters.operation,
      year,
      grade
    ],
    queryFn: async () => {
      if (isSchool && login.entityLevel) {
        const response = await api.get_Assessment_JustTheFactsAggregateByLevels(
          {
            id: id!,
            level: login.entityLevel,
            jtfMode: filters.level,
            operation: filters.operation,
            year: year!,
            grade,
            aggregateLevel: $EntityLevel.from(login.entityLevel - 1)
          }
        )
        return { type: 'school' as const, data: response.data || [] }
      } else {
        const response = await api.get_Assessment_JustTheFactsTeam({
          teamId: id as TeamId,
          jtfMode: filters.level
        })
        return { type: 'team' as const, data: response.teamJTFs || [] }
      }
    },
    enabled: id != null
  })

  const assessmentReport = getAssessmentReportData(
    reportData?.type === (isSchool ? 'school' : 'team')
      ? reportData.data
      : undefined
  )

  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const { table, Table } = useAssessmentTable<
    Database_Procs_TeamJTF_Item | Database_Procs_JTFAggregatedByLevel_Item
  >({
    data: assessmentReport,
    columns: memoColumns,
    isLoading,
    globalFilter: searchQuery,
    horizontalScroll: !isSchool,
    initialSorting: isSchool
      ? schoolAssessmentInitialSorting
      : teamAssessmentInitialSorting,
    highLightColumnsById: activeColumns[filters.operation - 1],
    highlightColumnsCell: true
  })
  const selectedRow = table.getSelectedRowModel().flatRows

  // Update localStorage whenever level,operation changes
  useEffect(() => {
    appSessionStorage.jtfLevel = filters.level
    appSessionStorage.jtfOperation = filters.operation
  }, [filters.level, filters.operation])

  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = (
    sendMessageUsers as { original: Database_Procs_TeamJTF_Item }[]
  ).map(({ original }) => ({
    label: original.displayName || original.userId,
    value: original.playerId
  }))
  const fileName = isSchool ? `${login.schoolName}_JTF` : `${team?.name}_JTF`
  const exportColumn = isSchool
    ? schoolHidableSelectLabel
    : newHidableSelectLabel

  const items = useDropdownMenu({
    table,
    hidableSelectLabel: exportColumn,
    fileName,
    hasColumnChange: isSchool
      ? [
          'preProficient',
          'postProficient',
          'numberFluentPretest',
          'numberFluent'
        ]
      : [],
    setIsLoading: setIsExporting
  })
  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay
          isLoading={isLoading || isExporting || isSlugLinksLoading}
        />
        <AssessmentReportNavigation
          exportButton={
            <ExportMenu
              ariaLabel="Export report options"
              items={items}
              trigger={['click']}
            />
          }
          filters={
            <Flex gap={14} wrap>
              <Flex flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">Level</p>
                <LevelSelect
                  value={filters.level}
                  onChange={(level) => {
                    setFilters((prev) => ({
                      ...prev,
                      level: $JtfMode.from(level)
                    }))
                    setSearchQuery('')
                  }}
                  options={jtfLevelOptions}
                />
              </Flex>
              <Flex flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">Operations</p>
                <LevelSelect
                  value={filters.operation}
                  onChange={(operation) => {
                    setFilters((prev) => ({
                      ...prev,
                      operation: $ArithmeticOperation.from(operation)
                    }))
                    setSearchQuery('')
                  }}
                  options={operationOptions}
                />
              </Flex>
            </Flex>
          }
          dropdownFilters={dropdownFilters}
          setDropDownFilters={setDropDownFilters}
          isSchool={isSchool}
        />

        <AssessmentReportSummaryDashboard
          dropdownFilters={dropdownFilters}
          isSchool={isSchool}
        />
        <Flex
          vertical
          className="graph-tiles"
          justify="space-between"
          gap="20px"
        >
          <Flex
            align="center"
            className="graph-tiles__title"
            justify="space-between"
          >
            <h4 className="title">Student Data</h4>
          </Flex>
          <Flex vertical align="center" className="graph-tiles__graph">
            <div className="graph-container">
              <AssessmentJTFCharts chartData={summaryRecordData} />
            </div>
          </Flex>
        </Flex>
        {/* result */}
        <div className="main-result">
          {/* Updated toolbar design */}
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">
              {isSchool ? 'School Teams' : 'Team Players'}
            </h4>

            <Flex
              gap="10px"
              justify="space-between"
              wrap
              className="print-hidden"
            >
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              {!isSchool && (
                <Button
                  intent="tertiary"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => setIsModalOpen(true)}
                >
                  SEND MESSAGE
                </Button>
              )}
            </Flex>
            <Table />
          </Flex>
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          level={jtfLevelOptions[filters.level]?.label}
          linkOptions={linkOptions}
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)
            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
          reportType="Just the Facts"
        />
      </Flex>
    </>
  )
}

export default AssessmentReportJTF
